class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  /* Boop v5.0.2: never fail silently after Add to cart.
     If the add response is missing a section (or it can't be parsed), fetch fresh copies,
     and always open the drawer, so the header count, shipping bar and totals can't go stale. */
  renderContents(parsedState) {
    const inner = this.querySelector('.drawer__inner');
    if (inner && inner.classList.contains('is-empty')) inner.classList.remove('is-empty');
    this.productId = parsedState && parsedState.id;

    const sections = (parsedState && parsedState.sections) || {};
    const applied = this.applySections(sections);

    if (applied) {
      this.afterRender();
      return;
    }

    BoopCartRefresh.fetchSections()
      .then((fresh) => this.applySections(fresh || {}))
      .catch((e) => console.error(e))
      .finally(() => this.afterRender());
  }

  // Returns true only if every section was replaced.
  applySections(sections) {
    let ok = true;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;

      const html = sections[section.id] ? this.getSectionInnerHTML(sections[section.id], section.selector) : null;
      if (html === null) {
        ok = false;
        return;
      }
      sectionElement.innerHTML = html;
    });
    return ok;
  }

  afterRender() {
    const empty = !this.querySelector('.cart-item');
    this.classList.toggle('is-empty', empty);
    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    try {
      const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      return el ? el.innerHTML : null;
    } catch (e) {
      return null;
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

/* Boop v5.0.2: one place that fetches fresh drawer + cart-icon HTML and swaps the WHOLE drawer
   (header count, shipping bar, items, suggestions, totals, Check out) instead of only some parts. */
const BoopCartRefresh = {
  fetchSections() {
    return fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`).then((r) => r.json());
  },
  apply(sections) {
    if (!sections) return;
    const parse = (html, selector) => {
      if (!html) return null;
      const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      return el ? el.innerHTML : null;
    };
    const drawerTarget = document.querySelector('#CartDrawer .drawer__inner');
    const drawerHTML = parse(sections['cart-drawer'], '.drawer__inner');
    if (drawerTarget && drawerHTML !== null) {
      const scroller = drawerTarget.querySelector('.boop-drawer__scroll');
      const keepScroll = scroller ? scroller.scrollTop : 0;
      drawerTarget.innerHTML = drawerHTML;
      const newScroller = drawerTarget.querySelector('.boop-drawer__scroll');
      if (newScroller) newScroller.scrollTop = keepScroll;
      const drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.classList.toggle('is-empty', !drawerTarget.querySelector('.cart-item'));
    }
    const bubble = document.getElementById('cart-icon-bubble');
    const bubbleHTML = parse(sections['cart-icon-bubble'], '.shopify-section');
    if (bubble && bubbleHTML !== null) bubble.innerHTML = bubbleHTML;
  },
};
window.BoopCartRefresh = BoopCartRefresh;

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  // Dawn only refreshed the item list and totals block here; refresh the whole drawer + cart icon.
  onCartUpdate() {
    return BoopCartRefresh.fetchSections()
      .then((sections) => BoopCartRefresh.apply(sections))
      .catch((e) => console.error(e));
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
